import React, { useEffect, useState } from "react";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  LOGIN_ROUTE,
  setCurrentStep,
  setOtpSent,
  checkPhoneNumberExists,
  sendOTP,
  signUp,
  validateOtp,
} from "@hypexone/uicore";
import StepOne from "./StepOne";
import Button from "../../components/shared/Button/Button";
import SocialAuth from "../social-auth/SocialAuth";


const SignUpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { otpSent, phoneNumberExists } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<number>();
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get("campaignId");
  const [campaignCode, setCampaignCode] = useState<string>(campaignId ?? "");
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const maxLength = 10;
    if (newValue.length <= maxLength && /^\d*$/.test(newValue)) {
      setPhoneNumber(newValue);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const showPasswordClickHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const rawCamp = campaignCode ?? "";
    const cleanCamp = rawCamp.trim().toLowerCase();
    setLoader(true);

    try {
      if (!otpSent && phoneNumber && agreedToTerms) {
        await dispatch(sendOTP("91" + phoneNumber)).unwrap();
      } else if (otpSent && otp && (!username || !password)) {
        await dispatch(
          validateOtp({ phoneNumber: `91${phoneNumber}`, otp })
        ).unwrap();
      } else if (otpSent && username && password) {
        await dispatch(
          signUp({
            username: username.toLowerCase(),
            fullName: username.toLowerCase(),
            emailId: email,
            password,
            phoneNumber: "91" + phoneNumber,
            otp,
            referralCode: "",
            campaignCode: cleanCamp,
          })
        ).unwrap();
        navigate("/home");
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const redirectToSignIn = () => {
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    if (campaignId) {
      localStorage.setItem("campaignId", campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    dispatch(setCurrentStep(1));
    dispatch(setOtpSent(false));
  }, []);

  useEffect(() => {
    if (phoneNumber) {
      dispatch(checkPhoneNumberExists(phoneNumber));
    }
  }, [phoneNumber]);

  const isSubmitDisabled =
    !phoneNumber ||
    !agreedToTerms ||
    !isUsernameValid ||
    phoneNumberExists ||
    (otpSent && (!otp || !username || !password)) ||
    loader;

  return (
    <main className="h-full w-full flex flex-col px-4 pt-3 pb-5">
      <div className="relative w-full max-w-md mx-auto flex flex-col flex-1 min-h-0">
        {/* Back button */}
        <Button
          onClick={redirectToSignIn}
          className="shrink-0 self-start flex items-center gap-2 text-sm font-medium text-white hover:text-yellow-300 transition-all"
        >
          <FaArrowLeftLong className="text-base" />
          <span>{langData?.["back"]}</span>
        </Button>

        {/* Header */}
        <header className="flex flex-col justify-center items-center text-center mt-16 mb-6 shrink-0">
          <h1 className="text-3xl font-extrabold text-white drop-shadow-md">
            {langData?.["sign_up"]}
          </h1>
          <p className="text-base text-gray-200 mt-2">
            {langData?.["create_your_account_txt"]}
          </p>
        </header>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0 w-full text-sm font-medium overflow-y-auto hide-scrollbar"
        >
          <StepOne
            otpSent={otpSent}
            otp={otp}
            setPhoneNumber={setPhoneNumber}
            phoneNumber={phoneNumber}
            agreedToTerms={agreedToTerms}
            email={email}
            onEmailChange={handleEmailChange}
            onPhoneChange={handlePhoneChange}
            onTermsChange={handleTermsChange}
            username={username}
            password={password}
            setOtp={setOtp}
            showPassword={showPassword}
            onUsernameChange={handleUsernameChange}
            onPasswordChange={handlePasswordChange}
            showPasswordClickHandler={showPasswordClickHandler}
            onUsernameValidityChange={setIsUsernameValid}
            campaignCode={campaignCode}
            onCampaignCodeChange={setCampaignCode}
          />

          {/* Submit button */}
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            loading={loader}
            className={`w-full py-3 mt-5 text-base font-semibold text-center text-white rounded-xl min-h-[44px] shadow-lg transition-all duration-200 ${isSubmitDisabled
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:opacity-90"
              }`}
          >
            {!otpSent
              ? langData?.["send_otp"]
              : otpSent && (!username || !password)
                ? langData?.["validate_otp"]
                : langData?.["sign_up"]}
          </Button>

          {/* Social auth */}
          {(window.location.hostname.includes("crypto247") ||
            window.location.hostname.includes("anna555")) && (
              <div className="flex flex-col items-center text-gray-100 space-y-3 mt-4">
                <span className="text-sm">or continue with</span>
                <SocialAuth />
              </div>
            )}

          {/* Already have an account */}
          <div className="self-center mt-10 text-base text-center text-gray-200">
            {langData?.["already_have_an_account"]}{" "}
            <a
              href="#"
              onClick={redirectToSignIn}
              className="text-yellow-300 underline font-semibold hover:text-yellow-400 transition-all duration-200"
            >
              {langData?.["sign_in"]}
            </a>
          </div>
        </form>
      </div>
    </main>
  );
};

export default SignUpForm;
